// screens/appendice/QuestionProofCard.tsx — Appendice restructure
// (founder-locked 2026-07-14): the ONE official-question card style, shared by
// the argomento-level browser (QuestionBrowser) and the per-sotto-argomento
// embedded "Domande d'esame" section, so questions look identical everywhere:
// statement (+ optional figure) → VERO/FALSO pill → collapsible "Perché?" proof.
//
// Extracted verbatim from QuestionBrowser's QuestionRow; the one addition is
// `preferPrincipleId`: when the card renders inside a sotto-argomento, the
// proof shown is the one written for THAT principle (falling back to the first
// proof), so the "Perché" always speaks about the rule the student is reading.

import { useState, type JSX } from 'react';
import { useTokens } from '../../state/theme';
import { InlineImage } from '../../components/InlineImage';
import { SelectProfFigureButton } from '../../components/SelectProfFigureButton';
import { principleIdMatches } from '../../api/appendice';
import type { ArgomentoQuestionDto } from '../../api/kb';

export function QuestionProofCard({
  q,
  argomentoId,
  preferPrincipleId,
  hueColor,
}: {
  q: ArgomentoQuestionDto;
  /** Needed only with preferPrincipleId (short/full id normalization). */
  argomentoId?: string;
  /** Prefer the proof mapped to this principle (sotto-argomento embedding). */
  preferPrincipleId?: string;
  /** Restyle (2026-07-14): the argomento's identity hue — left edge + Perché.
   *  Never the VERO/FALSO pills: those stay semantic green/rose. */
  hueColor?: string;
}): JSX.Element {
  const tokens = useTokens();
  const [revealed, setRevealed] = useState(false);
  const isVero = q.is_true;
  const chipBg = isVero ? tokens.rightSoft : tokens.wrongSoft;
  const chipText = isVero ? tokens.rightText : tokens.wrongText;
  const preferred = preferPrincipleId && argomentoId
    ? q.proofs.find((p) => principleIdMatches(p.principle_id, argomentoId, preferPrincipleId))
    : undefined;
  const proof = preferred?.proof_text ?? q.proofs[0]?.proof_text;

  return (
    <div
      data-testid={`question-card-${q.id}`}
      style={{
        background: tokens.surface, border: `1px solid ${tokens.line}`,
        borderLeft: hueColor ? `3px solid ${hueColor}` : `1px solid ${tokens.line}`,
        borderRadius: tokens.radius.md, padding: '12px 14px',
      }}
    >
      <div style={{ display: 'flex', gap: 10 }}>
        {q.image_ref && (
          <div style={{ flexShrink: 0, width: 64 }}>
            <InlineImage kind="question" id={q.id} imageRef={q.image_ref} altOverride="Figura della domanda" />
          </div>
        )}
        <p style={{ flex: 1, margin: 0, fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.text, lineHeight: 1.45 }}>
          {q.text_it}
        </p>
      </div>

      {q.image_ref && <SelectProfFigureButton selection={{
        reference: { kind: 'question', questionId: q.id },
        imageRef: q.image_ref, label: 'Figura della domanda',
      }} />}

      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span
          data-testid={`answer-${q.id}`}
          style={{ background: chipBg, color: chipText, fontFamily: tokens.fonts.mono, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: '3px 9px', borderRadius: tokens.radius.pill }}
        >
          {isVero ? 'VERO' : 'FALSO'}
        </span>
        {proof && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-expanded={revealed}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 2px',
              fontFamily: tokens.fonts.body, fontSize: 12.5, fontWeight: 600, color: hueColor ?? tokens.accent,
            }}
          >
            {revealed ? 'Nascondi spiegazione' : 'Perché?'}
          </button>
        )}
      </div>

      {revealed && proof && (
        <div
          data-testid={`proof-${q.id}`}
          style={{
            marginTop: 8, background: tokens.bg, border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md, padding: '10px 12px',
            fontFamily: tokens.fonts.body, fontSize: 13.5, color: tokens.textMuted, lineHeight: 1.5,
          }}
        >
          {proof}
        </div>
      )}
    </div>
  );
}
